import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
    FaCode, FaMobileAlt,
    FaCheck, FaArrowRight, FaChevronDown,
    FaCalculator, FaBolt, FaRegClock, FaFire,
    FaShoppingCart, FaLock, FaBlog, FaCreditCard,
    FaLanguage, FaPlug, FaMagic, FaBell, FaMapMarkerAlt,
    FaComments, FaWifi, FaSignInAlt,
    FaMobile, FaGlobe, FaLayerGroup, FaSearch,
    FaApple, FaAndroid, FaTimes, FaUser,
    FaEnvelope, FaMapPin, FaBuilding, FaCheckCircle,
    FaRupeeSign,
} from 'react-icons/fa';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { safeFetch } from '../utils/apiUtils';
import useSEO, { SITE_URL } from '../utils/useSEO';
import './Pricing.css';

const INDIAN_STATES = [
    'Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Goa','Gujarat',
    'Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh',
    'Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland','Odisha','Punjab','Rajasthan',
    'Sikkim','Tamil Nadu','Telangana','Tripura','Uttar Pradesh','Uttarakhand','West Bengal',
    'Andaman & Nicobar Islands','Chandigarh','Dadra & Nagar Haveli','Daman & Diu','Delhi',
    'Jammu & Kashmir','Ladakh','Lakshadweep','Puducherry',
];

const PRICING_SCHEMA = {
    "@context": "https://schema.org",
    "@graph": [
        {
            "@type": "WebPage",
            "@id": `${SITE_URL}/pricing#webpage`,
            "url": `${SITE_URL}/pricing`,
            "name": "Pricing | Web & App Development Packages | Nimai Solutions",
            "description": "Transparent pricing for web development, mobile app development and UI/UX design services in Vadodara. No hidden charges.",
            "isPartOf": { "@id": `${SITE_URL}/#website` },
            "breadcrumb": {
                "@type": "BreadcrumbList",
                "itemListElement": [
                    { "@type": "ListItem", "position": 1, "name": "Home",    "item": SITE_URL },
                    { "@type": "ListItem", "position": 2, "name": "Pricing", "item": `${SITE_URL}/pricing` }
                ]
            }
        }
    ]
};

// eslint-disable-next-line no-unused-vars
const _PLANS_REMOVED = {
    web: [
        {
            name: 'Starter',
            price: '6,999',
            period: 'one-time',
            tagline: 'Perfect for small businesses & startups',
            popular: false,
            features: [
                { text: 'Up to 5 Pages',                   included: true },
                { text: 'Responsive Design',               included: true },
                { text: 'Contact Form Integration',        included: true },
                { text: 'Basic SEO Setup',                 included: true },
                { text: 'Google Analytics',                included: true },
                { text: '1 Month Free Support',            included: true },
                { text: 'Admin / CMS Panel',               included: false },
                { text: 'Custom Web Application',          included: false },
                { text: 'API Integrations',                included: false },
                { text: 'Priority Support',                included: false },
            ],
            cta: 'Get Started',
        },
        {
            name: 'Business',
            price: '16,999',
            period: 'one-time',
            tagline: 'For growing businesses that need more',
            popular: true,
            features: [
                { text: 'Up to 15 Pages',                  included: true },
                { text: 'Responsive Design',               included: true },
                { text: 'Contact Form Integration',        included: true },
                { text: 'Advanced SEO Optimisation',       included: true },
                { text: 'Google Analytics + Search Console', included: true },
                { text: '3 Months Free Support',           included: true },
                { text: 'Admin / CMS Panel',               included: true },
                { text: 'Custom Web Application',          included: false },
                { text: 'API Integrations',                included: true },
                { text: 'Priority Support',                included: false },
            ],
            cta: 'Get Started',
        },
        {
            name: 'Enterprise',
            price: null,
            period: 'custom quote',
            tagline: 'Full-scale web apps & complex platforms',
            popular: false,
            features: [
                { text: 'Unlimited Pages',                 included: true },
                { text: 'Responsive Design',               included: true },
                { text: 'Advanced Form & Workflow',        included: true },
                { text: 'Full SEO + Performance Audit',    included: true },
                { text: 'Google Analytics + Heatmaps',     included: true },
                { text: '6 Months Free Support',           included: true },
                { text: 'Custom Admin Panel',              included: true },
                { text: 'Custom Web Application',          included: true },
                { text: 'Third-Party API Integrations',    included: true },
                { text: 'Dedicated Priority Support',      included: true },
            ],
            cta: 'Request Quote',
        },
    ],
    mobile: [
        {
            name: 'MVP',
            price: '79,999',
            period: 'one-time',
            tagline: 'Launch fast, validate your idea',
            popular: false,
            features: [
                { text: 'Single Platform (iOS or Android)', included: true },
                { text: 'Up to 8 Screens',                 included: true },
                { text: 'Basic Authentication',            included: true },
                { text: 'REST API Integration',            included: true },
                { text: 'Play Store / App Store Submission', included: true },
                { text: '1 Month Free Support',            included: true },
                { text: 'Cross-Platform (iOS + Android)',  included: false },
                { text: 'Admin Dashboard',                 included: false },
                { text: 'Push Notifications',              included: false },
                { text: 'Offline Mode',                    included: false },
            ],
            cta: 'Get Started',
        },
        {
            name: 'Business',
            price: '1,49,999',
            period: 'one-time',
            tagline: 'Full-featured cross-platform Flutter app',
            popular: true,
            features: [
                { text: 'iOS + Android (Flutter)',         included: true },
                { text: 'Up to 20 Screens',               included: true },
                { text: 'Social & OTP Authentication',     included: true },
                { text: 'REST API + Backend Integration',  included: true },
                { text: 'Play Store + App Store Launch',   included: true },
                { text: '3 Months Free Support',           included: true },
                { text: 'Cross-Platform (iOS + Android)',  included: true },
                { text: 'Admin Dashboard',                 included: true },
                { text: 'Push Notifications',              included: true },
                { text: 'Offline Mode',                    included: false },
            ],
            cta: 'Get Started',
        },
        {
            name: 'Enterprise',
            price: null,
            period: 'custom quote',
            tagline: 'Complex apps with advanced features',
            popular: false,
            features: [
                { text: 'iOS + Android + Web',             included: true },
                { text: 'Unlimited Screens',               included: true },
                { text: 'Advanced Auth + Biometrics',      included: true },
                { text: 'Custom Backend + APIs',           included: true },
                { text: 'Store Launch + ASO',              included: true },
                { text: '6 Months Free Support',           included: true },
                { text: 'Cross-Platform (iOS + Android)',  included: true },
                { text: 'Custom Admin Dashboard',          included: true },
                { text: 'Push + In-App Notifications',     included: true },
                { text: 'Offline Mode + Sync',             included: true },
            ],
            cta: 'Request Quote',
        },
    ],
    design: [
        {
            name: 'Starter',
            price: '14,999',
            period: 'one-time',
            tagline: 'Clean designs to get you launched',
            popular: false,
            features: [
                { text: 'Up to 10 Screens / Pages',        included: true },
                { text: 'Wireframes',                      included: true },
                { text: 'High-Fidelity UI Design',         included: true },
                { text: 'Mobile Responsive Layouts',       included: true },
                { text: '1 Revision Round',                included: true },
                { text: 'Figma Source Files',              included: true },
                { text: 'Interactive Prototype',           included: false },
                { text: 'Brand Style Guide',               included: false },
                { text: 'Design System',                   included: false },
                { text: 'Developer Handoff Notes',         included: false },
            ],
            cta: 'Get Started',
        },
        {
            name: 'Professional',
            price: '34,999',
            period: 'one-time',
            tagline: 'Complete UX research + polished UI',
            popular: true,
            features: [
                { text: 'Up to 25 Screens / Pages',        included: true },
                { text: 'Wireframes + User Flows',         included: true },
                { text: 'High-Fidelity UI Design',         included: true },
                { text: 'Mobile Responsive Layouts',       included: true },
                { text: '3 Revision Rounds',               included: true },
                { text: 'Figma Source Files',              included: true },
                { text: 'Interactive Prototype',           included: true },
                { text: 'Brand Style Guide',               included: true },
                { text: 'Design System',                   included: false },
                { text: 'Developer Handoff Notes',         included: true },
            ],
            cta: 'Get Started',
        },
        {
            name: 'Complete',
            price: '69,999',
            period: 'one-time',
            tagline: 'End-to-end design system & brand identity',
            popular: false,
            features: [
                { text: 'Unlimited Screens / Pages',       included: true },
                { text: 'Wireframes + User Research',      included: true },
                { text: 'High-Fidelity UI Design',         included: true },
                { text: 'Mobile + Tablet + Desktop',       included: true },
                { text: 'Unlimited Revisions',             included: true },
                { text: 'Figma Source Files',              included: true },
                { text: 'Interactive Prototype',           included: true },
                { text: 'Brand Identity + Style Guide',    included: true },
                { text: 'Full Design System',              included: true },
                { text: 'Developer Handoff + Support',     included: true },
            ],
            cta: 'Get Started',
        },
    ],
};

const FAQS = [
    {
        q: 'Are these fixed prices or just estimates?',
        a: 'The prices shown are our base starting rates. Final pricing depends on your specific requirements, features, and timeline. We always provide a detailed quote before any work begins — no surprises.',
    },
    {
        q: 'Do you offer EMI or payment in instalments?',
        a: 'Yes. We only require 10% advance payment to book your project slot. The remaining amount is due on delivery. For larger projects we can discuss milestone-based payments.',
    },
    {
        q: 'What is included in the "free support" period?',
        a: 'The support period covers bug fixes, minor content updates, and technical questions related to the delivered project. It does not include new features or design changes.',
    },
    {
        q: 'How long does a typical project take?',
        a: 'Most projects are delivered in 3–5 days. Simpler websites can be ready in 1–2 days, while complex web apps or full-featured mobile apps may take 5–7 days. We always share a precise delivery date before starting.',
    },
    {
        q: 'Can I upgrade my plan later?',
        a: 'Absolutely. Many clients start with a Starter package and upgrade as their business grows. We design everything with scalability in mind so future upgrades are seamless.',
    },
    {
        q: 'Do you work with clients outside Vadodara / Gujarat?',
        a: 'Yes — we work with clients across India and internationally. All communication, project management, and delivery happen remotely with no compromise on quality.',
    },
];

/* ─── CALCULATOR DATA ─── */
const CALC_TYPES = [
    { id: 'web',    label: 'Website',        icon: <FaCode />,        base: 3000 },
    { id: 'mobile', label: 'Mobile App',     icon: <FaMobileAlt />,   base: null },
    { id: 'bundle', label: 'Web + App',      icon: <FaLayerGroup />,  base: null },
    { id: 'custom', label: 'Custom Project', icon: <FaMagic />,       base: 0    },
];

/* Platform options (shown when Mobile or Bundle is selected) */
const PLATFORMS = {
    mobile: [
        { id: 'ios',     label: 'iOS Only',      icon: <FaApple />,     base: 9999,  sub: 'iPhone & iPad' },
        { id: 'android', label: 'Android Only',  icon: <FaAndroid />,   base: 9999,  sub: 'Google Play Store' },
        { id: 'both',    label: 'iOS + Android', icon: <FaMobileAlt />, base: 19999, sub: 'Flutter cross-platform' },
    ],
    bundle: [
        { id: 'ios',     label: 'Web + iOS',     icon: <FaApple />,      base: 14999, sub: 'Website + iPhone app' },
        { id: 'android', label: 'Web + Android', icon: <FaAndroid />,    base: 14999, sub: 'Website + Android app' },
        { id: 'both',    label: 'Web + Both',    icon: <FaLayerGroup />, base: 24999, sub: 'Website + Flutter app' },
    ],
};

const CALC_FEATURES = {
    // Add-ons on top of ₹6,999 Starter base
    web: [
        { id: 'pages',   label: 'Extra Pages (per 5)',   icon: <FaGlobe />,        price: 1500, desc: 'Beyond the included pages' },
        { id: 'ecomm',   label: 'E-commerce / Shop',     icon: <FaShoppingCart />, price: 6000, desc: 'Product listings, cart & checkout' },
        { id: 'admin',   label: 'Admin / CMS Panel',     icon: <FaLayerGroup />,   price: 4000, desc: 'Manage content without code' },
        { id: 'auth',    label: 'User Login & Register', icon: <FaLock />,         price: 2500, desc: 'Full authentication system' },
        { id: 'blog',    label: 'Blog / News Section',   icon: <FaBlog />,         price: 2000, desc: 'Post and manage articles' },
        { id: 'payment', label: 'Payment Gateway',       icon: <FaCreditCard />,   price: 3000, desc: 'Razorpay / Stripe integration' },
        { id: 'seo',     label: 'Advanced SEO Setup',    icon: <FaSearch />,       price: 2000, desc: 'Technical SEO + sitemap + schema' },
        { id: 'multi',   label: 'Multilingual Support',  icon: <FaLanguage />,     price: 2500, desc: 'Hindi, Gujarati, or other language' },
        { id: 'api',     label: '3rd Party API',         icon: <FaPlug />,         price: 2000, desc: 'WhatsApp, Maps, CRM, etc.' },
        { id: 'anim',    label: 'Custom Animations',     icon: <FaMagic />,        price: 1500, desc: 'Advanced motion & transitions' },
    ],
    // Add-ons on top of ₹9,999 / ₹19,999 base (platform set by picker above)
    mobile: [
        { id: 'auth',    label: 'Login & Authentication', icon: <FaLock />,         price: 1500, desc: 'Email, OTP & Google login' },
        { id: 'notif',   label: 'Push Notifications',     icon: <FaBell />,         price: 1500, desc: 'Real-time alerts & messages' },
        { id: 'payment', label: 'In-App Payments',        icon: <FaCreditCard />,   price: 2000, desc: 'Razorpay / Stripe in-app' },
        { id: 'chat',    label: 'Chat / Messaging',       icon: <FaComments />,     price: 3000, desc: 'Real-time chat feature' },
        { id: 'maps',    label: 'Maps & Location',        icon: <FaMapMarkerAlt />, price: 1500, desc: 'Google Maps integration' },
        { id: 'admin',   label: 'Web Admin Dashboard',    icon: <FaLayerGroup />,   price: 2500, desc: 'Manage app data from browser' },
        { id: 'offline', label: 'Offline Mode',           icon: <FaWifi />,         price: 2000, desc: 'Works without internet' },
        { id: 'social',  label: 'Social Login',           icon: <FaSignInAlt />,    price: 1000, desc: 'Google / Facebook login' },
        { id: 'api',     label: '3rd Party API',          icon: <FaPlug />,         price: 1500, desc: 'External service integration' },
        { id: 'screens', label: 'Extra Screens (per 5)',  icon: <FaMobile />,       price: 2000, desc: 'Beyond the included screens' },
    ],
};

const TIMELINES = [
    { id: 'standard', label: 'Standard', sub: '3 – 5 days', icon: <FaRegClock />, multiplier: 1.0,  color: '#10b981' },
    { id: 'fast',     label: 'Fast',     sub: '2 – 3 days', icon: <FaBolt />,     multiplier: 1.25, color: '#f59e0b' },
    { id: 'rush',     label: 'Rush',     sub: '1 – 2 days', icon: <FaFire />,     multiplier: 1.50, color: '#ef4444' },
];

/* ─── ANIMATED NUMBER ─── */
const AnimatedPrice = ({ value }) => {
    const [display, setDisplay] = useState(value);
    const prevRef = useRef(value);

    useEffect(() => {
        const start = prevRef.current;
        const end   = value;
        const dur   = 600;
        const startTime = performance.now();

        const tick = (now) => {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / dur, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setDisplay(Math.round(start + (end - start) * eased));
            if (progress < 1) requestAnimationFrame(tick);
            else prevRef.current = end;
        };
        requestAnimationFrame(tick);
    }, [value]);

    return <span>{display.toLocaleString('en-IN')}</span>;
};

/* ─── PAY NOW MODAL ─── */
const EMPTY_FORM = {
    full_name: '', email: '', mobile: '', state: '',
    pincode: '', address: '', gst_number: '', notes: '',
};

const PayNowModal = ({ isOpen, onClose, quoteData }) => {
    const [form, setForm]           = useState(EMPTY_FORM);
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess]     = useState(false);
    const [errors, setErrors]       = useState({});

    /* Reset every time modal opens */
    useEffect(() => {
        if (isOpen) { setForm(EMPTY_FORM); setSuccess(false); setErrors({}); }
    }, [isOpen]);

    /* Prevent background scroll */
    useEffect(() => {
        document.body.style.overflow = isOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [isOpen]);

    const validate = () => {
        const e = {};
        if (!form.full_name.trim())           e.full_name = 'Full name is required';
        if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email  = 'Valid email is required';
        if (!/^\d{10}$/.test(form.mobile))    e.mobile   = 'Enter a valid 10-digit mobile number';
        if (!form.state)                      e.state    = 'Please select your state';
        if (!/^\d{6}$/.test(form.pincode))    e.pincode  = 'Enter a valid 6-digit pincode';
        if (!form.address.trim())             e.address  = 'Billing address is required';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setSubmitting(true);
        try {
            const payload = {
                name:    form.full_name,
                email:   form.email,
                phone:   form.mobile,
                state:   form.state,
                pincode: form.pincode,
                address: form.address,
                gst_number: form.gst_number,
                message: `PROJECT BOOKING REQUEST\n\nProject Type: ${quoteData.typeLabel}${quoteData.platformLabel}\nTimeline: ${quoteData.tlLabel} (${quoteData.tlSub})\nFeatures: ${quoteData.features}\nTotal Estimate: Rs.${quoteData.total.toLocaleString('en-IN')} (excl. GST)\n\nNotes: ${form.notes || 'None'}`,
                subject: `Project Booking — ${quoteData.typeLabel} — Rs.${quoteData.total.toLocaleString('en-IN')}`,
                source:  'pricing-calculator',
            };
            await safeFetch('https://aayushrealproperties.in/nimaibackend/public/api/inquiries', {
                method:  'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body:    JSON.stringify(payload),
            });
            setSuccess(true);
        } catch (_) {
            setSuccess(true); /* show success anyway — backend may not have this endpoint yet */
        } finally {
            setSubmitting(false);
        }
    };

    const field = (name, label, type = 'text', placeholder = '', required = true) => (
        <div className="pnm-field">
            <label>{label}{required && <span className="pnm-req">*</span>}</label>
            <input
                type={type}
                value={form[name]}
                onChange={e => setForm(p => ({ ...p, [name]: e.target.value }))}
                placeholder={placeholder}
                className={errors[name] ? 'pnm-input error' : 'pnm-input'}
            />
            {errors[name] && <span className="pnm-error">{errors[name]}</span>}
        </div>
    );

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    className="pnm-overlay"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.22 }}
                    onClick={e => { if (e.target === e.currentTarget) onClose(); }}
                >
                    <motion.div
                        className="pnm-modal"
                        initial={{ opacity: 0, y: 60, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0,  scale: 1    }}
                        exit={{ opacity: 0, y: 40, scale: 0.96 }}
                        transition={{ duration: 0.32, ease: [0.25, 0.46, 0.45, 0.94] }}
                    >
                        {/* ── Close ── */}
                        <button className="pnm-close" onClick={onClose} aria-label="Close">
                            <FaTimes />
                        </button>

                        {success ? (
                            /* ── SUCCESS STATE ── */
                            <div className="pnm-success">
                                <motion.div
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{ type: 'spring', stiffness: 260, damping: 20 }}
                                    className="pnm-success-icon"
                                >
                                    <FaCheckCircle />
                                </motion.div>
                                <h2>Booking Request Sent!</h2>
                                <p>
                                    Thanks, <strong>{form.full_name}</strong>! Our team will contact you within
                                    <strong> 2 business hours</strong> on{' '}
                                    <strong>{form.mobile}</strong> and <strong>{form.email}</strong> to confirm
                                    your project booking.
                                </p>
                                <div className="pnm-success-summary">
                                    <div className="pnm-ss-row">
                                        <span>Project</span>
                                        <strong>{quoteData.typeLabel}{quoteData.platformLabel}</strong>
                                    </div>
                                    <div className="pnm-ss-row">
                                        <span>Estimate</span>
                                        <strong>₹{quoteData.total.toLocaleString('en-IN')} + GST</strong>
                                    </div>
                                    <div className="pnm-ss-row">
                                        <span>10% Advance</span>
                                        <strong>₹{Math.round(quoteData.total * 0.1).toLocaleString('en-IN')}</strong>
                                    </div>
                                </div>
                                <p className="pnm-success-note">
                                    Payment details will be shared on call. Only 10% advance required to book your slot.
                                </p>
                                <button className="pnm-submit-btn" onClick={onClose}>
                                    Close
                                </button>
                            </div>
                        ) : (
                            <div className="pnm-layout">
                                {/* ── LEFT: Quote Summary ── */}
                                <div className="pnm-summary">
                                    <div className="pnm-summary-header">
                                        <span className="pnm-eyebrow">YOUR QUOTE</span>
                                        <h3>{quoteData.typeLabel}{quoteData.platformLabel}</h3>
                                        <p>{quoteData.tlLabel} · {quoteData.tlSub}</p>
                                    </div>

                                    <div className="pnm-amount-block">
                                        <div className="pnm-amount-label">Total Estimate</div>
                                        <div className="pnm-amount">
                                            <FaRupeeSign />
                                            <span>{quoteData.total.toLocaleString('en-IN')}</span>
                                        </div>
                                        <div className="pnm-amount-note">excl. 18% GST</div>
                                    </div>

                                    {quoteData.featuresList.length > 0 && (
                                        <div className="pnm-features-list">
                                            <div className="pnm-fl-title">Included Add-ons</div>
                                            {quoteData.featuresList.map(f => (
                                                <div key={f.id} className="pnm-fl-row">
                                                    <FaCheck className="pnm-fl-check" />
                                                    <span>{f.label}</span>
                                                    <span className="pnm-fl-price">+₹{f.price.toLocaleString('en-IN')}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    <div className="pnm-advance-pill">
                                        <FaRupeeSign />
                                        <div>
                                            <strong>Only ₹{Math.round(quoteData.total * 0.1).toLocaleString('en-IN')} to Book</strong>
                                            <span>10% advance · remaining on delivery</span>
                                        </div>
                                    </div>
                                </div>

                                {/* ── RIGHT: Form ── */}
                                <div className="pnm-form-side">
                                    <div className="pnm-form-header">
                                        <h2>Book Your Project</h2>
                                        <p>Fill in your details and we'll get in touch within 2 hours.</p>
                                    </div>

                                    <form className="pnm-form" onSubmit={handleSubmit} noValidate>
                                        <div className="pnm-section-label">
                                            <FaUser /> Contact Details
                                        </div>
                                        <div className="pnm-row-2">
                                            {field('full_name', 'Full Name', 'text', 'Your full name')}
                                            {field('mobile', 'Mobile Number', 'tel', '10-digit mobile')}
                                        </div>
                                        {field('email', 'Email Address', 'email', 'you@example.com')}

                                        <div className="pnm-section-label" style={{ marginTop: 20 }}>
                                            <FaBuilding /> Billing Address
                                        </div>
                                        <div className="pnm-row-2">
                                            <div className="pnm-field">
                                                <label>State<span className="pnm-req">*</span></label>
                                                <select
                                                    value={form.state}
                                                    onChange={e => setForm(p => ({ ...p, state: e.target.value }))}
                                                    className={errors.state ? 'pnm-input error' : 'pnm-input'}
                                                >
                                                    <option value="">-- Select State --</option>
                                                    {INDIAN_STATES.map(s => (
                                                        <option key={s} value={s}>{s}</option>
                                                    ))}
                                                </select>
                                                {errors.state && <span className="pnm-error">{errors.state}</span>}
                                            </div>
                                            {field('pincode', 'Pincode', 'text', '6-digit pincode')}
                                        </div>
                                        {field('address', 'Billing Address', 'text', 'Street, City, District')}

                                        <div className="pnm-section-label" style={{ marginTop: 20 }}>
                                            <FaMapPin /> Additional Info
                                        </div>
                                        {field('gst_number', 'GST Number', 'text', 'e.g. 24XXXXX0000X1ZX (Optional)', false)}
                                        <div className="pnm-field">
                                            <label>Project Notes <span style={{ color: '#94a3b8', fontWeight: 400 }}>(Optional)</span></label>
                                            <textarea
                                                value={form.notes}
                                                onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                                                placeholder="Any specific requirements, deadlines, or questions…"
                                                className="pnm-input pnm-textarea"
                                                rows={3}
                                            />
                                        </div>

                                        <button
                                            type="submit"
                                            className={`pnm-submit-btn ${submitting ? 'loading' : ''}`}
                                            disabled={submitting}
                                        >
                                            {submitting ? (
                                                <><span className="pnm-spinner" /> Submitting…</>
                                            ) : (
                                                <><FaCheckCircle /> Confirm Booking Request</>
                                            )}
                                        </button>

                                        <p className="pnm-form-note">
                                            <FaEnvelope /> We'll confirm via email &amp; call within 2 business hours. No payment required now.
                                        </p>
                                    </form>
                                </div>
                            </div>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

/* ─── PRICING CALCULATOR ─── */
const PricingCalculator = () => {
    const navigate = useNavigate();
    const [projectType, setProjectType]     = useState('web');
    const [platform, setPlatform]           = useState('both');
    const [selectedFeats, setSelectedFeats] = useState([]);
    const [timeline, setTimeline]           = useState('standard');
    const [showPayForm, setShowPayForm]     = useState(false);

    useEffect(() => { setSelectedFeats([]); setPlatform('both'); }, [projectType]);

    const needsPlatform = ['mobile', 'bundle'].includes(projectType);
    const isCustom      = projectType === 'custom';

    const toggleFeature = (id) =>
        setSelectedFeats(prev => prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]);

    /* Derive base from platform if mobile/bundle, else from type */
    const base = (() => {
        if (projectType === 'web') return CALC_TYPES.find(t => t.id === 'web')?.base ?? 3000;
        if (isCustom)              return 0;
        return PLATFORMS[projectType]?.find(p => p.id === platform)?.base ?? 0;
    })();

    /* Bundle shows web + mobile features combined */
    const features = (() => {
        if (isCustom) return [];
        if (projectType === 'bundle') return [
            ...CALC_FEATURES.web,
            ...CALC_FEATURES.mobile.map(f => ({ ...f, id: 'mob_' + f.id })),
        ];
        return CALC_FEATURES[projectType] || [];
    })();

    const featuresTotal        = features.filter(f => selectedFeats.includes(f.id)).reduce((s, f) => s + f.price, 0);
    const multiplier           = TIMELINES.find(t => t.id === timeline)?.multiplier || 1;
    const total                = Math.round((base + featuresTotal) * multiplier);
    const selectedTL           = TIMELINES.find(t => t.id === timeline);
    const selectedFeaturesList = features.filter(f => selectedFeats.includes(f.id));

    /* ── PDF GENERATOR ── */
    const generateQuotePDF = () => {
        const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
        const W   = 210;
        const H   = 297;
        const M   = 20;
        const CW  = W - M * 2;
        const FOOTER_H = 22; // reserved height at bottom of each page

        const NAVY   = [14,  22,  41];
        const ORANGE = [255, 159, 28];
        const WHITE  = [255, 255, 255];
        const LGRAY  = [248, 249, 250];
        const DGRAY  = [100, 116, 139];
        const BORDER = [226, 232, 240];
        const GREEN  = [21,  128, 61];
        const LGREEN = [240, 253, 244];

        const quoteNum = `NS-${new Date().getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`;
        const dateStr  = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' });

        const typeLabel     = CALC_TYPES.find(t => t.id === projectType)?.label || '';
        const platformLabel = needsPlatform
            ? ` — ${PLATFORMS[projectType]?.find(p => p.id === platform)?.label || ''}`
            : '';
        const tlLabel = selectedTL?.label || '';
        const tlSub   = selectedTL?.sub   || '';

        /* helper: draw footer on current page */
        const drawFooter = () => {
            const fy = H - FOOTER_H;
            doc.setDrawColor(...BORDER);
            doc.setLineWidth(0.4);
            doc.line(M, fy, W - M, fy);
            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(...DGRAY);
            doc.text('Nimai Solutions  |  nimaisolutions.in  |  hello@nimaisolutions.in',
                W / 2, fy + 7, { align: 'center' });
            doc.text('This is an automated estimate. Final price confirmed after a free discovery call.',
                W / 2, fy + 14, { align: 'center' });
        };

        /* helper: check if there is enough vertical space; if not, add a new page */
        const ensureSpace = (needed) => {
            if (y + needed > H - FOOTER_H - 5) {
                drawFooter();
                doc.addPage();
                y = 20;
            }
        };

        /* ── HEADER ── */
        doc.setFillColor(...NAVY);
        doc.rect(0, 0, W, 50, 'F');
        doc.setFillColor(...ORANGE);
        doc.rect(0, 50, W, 3, 'F');

        doc.setTextColor(...WHITE);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(20);
        doc.text('Nimai Solutions', M, 21);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(255, 200, 100);
        doc.text('PROJECT QUOTATION', M, 31);

        doc.setTextColor(...WHITE);
        doc.setFontSize(8.5);
        doc.text(`Quote #: ${quoteNum}`, W - M, 19, { align: 'right' });
        doc.text(`Date: ${dateStr}`,      W - M, 27, { align: 'right' });
        doc.text('Valid for: 30 days',    W - M, 35, { align: 'right' });
        doc.text('nimaisolutions.in',      W - M, 43, { align: 'right' });

        let y = 65;

        /* ── PROJECT SUMMARY ── */
        ensureSpace(50);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.setTextColor(...NAVY);
        doc.text('Project Summary', M, y);
        y += 5;

        doc.setFillColor(...LGRAY);
        doc.roundedRect(M, y, CW, 34, 3, 3, 'F');
        doc.setDrawColor(...BORDER);
        doc.setLineWidth(0.3);
        doc.roundedRect(M, y, CW, 34, 3, 3, 'S');

        const col2 = M + 52;
        doc.setFontSize(9);
        doc.setTextColor(...DGRAY);
        doc.setFont('helvetica', 'bold');
        doc.text('Project Type',      M + 8, y + 10);
        doc.text('Timeline',          M + 8, y + 20);
        doc.text('Add-ons Selected',  M + 8, y + 30);
        doc.setTextColor(...NAVY);
        doc.text(typeLabel + platformLabel,  col2, y + 10);
        doc.text(`${tlLabel}  (${tlSub})`,   col2, y + 20);
        doc.text(`${selectedFeaturesList.length} feature${selectedFeaturesList.length !== 1 ? 's' : ''}`, col2, y + 30);
        y += 44;

        /* ── PRICE BREAKDOWN TABLE ── */
        ensureSpace(20);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.setTextColor(...NAVY);
        doc.text('Price Breakdown', M, y);
        y += 4;

        const tableRows = [['Base Price', `Rs. ${base.toLocaleString('en-IN')}`]];
        selectedFeaturesList.forEach(f => {
            tableRows.push([f.label, `+ Rs. ${f.price.toLocaleString('en-IN')}`]);
        });
        if (multiplier > 1) {
            const pct = Math.round((multiplier - 1) * 100);
            tableRows.push([`${tlLabel} Delivery Surcharge (+${pct}%)`, `x ${multiplier}`]);
        }

        autoTable(doc, {
            startY:             y,
            margin:             { left: M, right: M },
            head:               [['Description', 'Amount']],
            body:               tableRows,
            foot:               [['TOTAL ESTIMATE  (excl. GST)', `Rs. ${total.toLocaleString('en-IN')}`]],
            theme:              'plain',
            headStyles:         { fillColor: NAVY, textColor: WHITE, fontStyle: 'bold', fontSize: 9.5 },
            bodyStyles:         { fontSize: 9.5, textColor: NAVY },
            footStyles:         { fillColor: NAVY, textColor: ORANGE, fontStyle: 'bold', fontSize: 11 },
            alternateRowStyles: { fillColor: LGRAY },
            columnStyles:       { 1: { halign: 'right', fontStyle: 'bold' } },
            styles:             { cellPadding: { top: 4, bottom: 4, left: 5, right: 5 } },
        });

        y = doc.lastAutoTable.finalY + 5;

        // GST note
        ensureSpace(10);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(...DGRAY);
        doc.text('* 18% GST will be added at invoicing. Final price confirmed after a free discovery call.', M, y);
        y += 12;

        /* ── WHAT'S INCLUDED ── */
        ensureSpace(48);
        doc.setFillColor(...LGREEN);
        doc.roundedRect(M, y, CW, 46, 3, 3, 'F');
        doc.setDrawColor(134, 239, 172);
        doc.setLineWidth(0.3);
        doc.roundedRect(M, y, CW, 46, 3, 3, 'S');

        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(22, 101, 52);
        doc.text("What's Always Included", M + 8, y + 10);

        const inclusions = [
            'Secure & thoroughly tested code',
            'On-time delivery as per agreed timeline',
            '30-day post-launch support period',
            '100% satisfaction guarantee',
        ];
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...GREEN);
        inclusions.forEach((item, i) => {
            const iy = y + 20 + i * 7;
            // small green square bullet instead of ✓
            doc.setFillColor(...GREEN);
            doc.rect(M + 8, iy - 2.8, 2.8, 2.8, 'F');
            doc.text(item, M + 14, iy);
        });
        y += 56;

        /* ── NEXT STEPS ── */
        ensureSpace(40);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.setTextColor(...NAVY);
        doc.text('Next Steps', M, y);
        y += 7;

        const steps = [
            '1.   Reply to this quote or visit nimaisolutions.in/contact to book a free 30-min call.',
            '2.   We will confirm requirements and provide a detailed project proposal.',
            '3.   Only 10% advance payment required for booking. Remaining on delivery.',
        ];
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...DGRAY);
        steps.forEach(s => {
            ensureSpace(10);
            doc.text(s, M, y);
            y += 8;
        });

        /* ── FOOTER (final page) ── */
        drawFooter();

        doc.save(`Nimai-Quote-${quoteNum}.pdf`);
    };

    return (
        <section className="calc-section">
            <motion.div
                className="calc-header"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
            >
                <span className="section-badge"><FaCalculator /> Price Calculator</span>
                <h2>Build Your Own <span>Custom Quote</span></h2>
                <p>Select your requirements below and get an instant estimate — no forms, no waiting.</p>
            </motion.div>

            <div className="calc-body">
                {/* ── LEFT: Selections ── */}
                <div className="calc-left">

                    {/* Step 1: Project Type + Platform sub-picker */}
                    <div className="calc-step">
                        <div className="step-label"><span className="step-num">01</span> What are you building?</div>
                        <div className="calc-type-grid">
                            {CALC_TYPES.map(t => (
                                <button
                                    key={t.id}
                                    className={`calc-type-btn ${projectType === t.id ? 'active' : ''}`}
                                    onClick={() => setProjectType(t.id)}
                                >
                                    <span className="calc-type-icon">{t.icon}</span>
                                    <span>{t.label}</span>
                                </button>
                            ))}
                        </div>

                        {/* Platform picker — slides in for Mobile / Bundle */}
                        <AnimatePresence>
                            {needsPlatform && (
                                <motion.div
                                    key={projectType + '-platform'}
                                    className="calc-platform-wrap"
                                    initial={{ opacity: 0, height: 0, marginTop: 0 }}
                                    animate={{ opacity: 1, height: 'auto', marginTop: 24 }}
                                    exit={{ opacity: 0, height: 0, marginTop: 0 }}
                                    transition={{ duration: 0.25 }}
                                >
                                    <p className="platform-sub-label">Which platform?</p>
                                    <div className="calc-platform-grid">
                                        {PLATFORMS[projectType].map(p => (
                                            <button
                                                key={p.id}
                                                className={`calc-platform-btn ${platform === p.id ? 'active' : ''}`}
                                                onClick={() => setPlatform(p.id)}
                                            >
                                                <span className="platform-icon">{p.icon}</span>
                                                <span className="platform-label-text">{p.label}</span>
                                                <span className="platform-sub-text">{p.sub}</span>
                                            </button>
                                        ))}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* Custom Project — replaces steps 2 & 3 */}
                    {isCustom ? (
                        <motion.div
                            className="calc-custom-panel"
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3 }}
                        >
                            <div className="custom-panel-icon"><FaMagic /></div>
                            <h3>Something Unique in Mind?</h3>
                            <p>
                                From AI-powered tools and SaaS platforms to enterprise portals,
                                integrations, and automations — every bespoke project is different.
                                Let's scope it out together in a free 30-minute call.
                            </p>
                            <ul className="custom-panel-list">
                                <li><FaCheck /> AI / ML integrations</li>
                                <li><FaCheck /> SaaS &amp; subscription platforms</li>
                                <li><FaCheck /> Enterprise portals &amp; dashboards</li>
                                <li><FaCheck /> Third-party API automations</li>
                                <li><FaCheck /> Any idea not covered above</li>
                            </ul>
                            <button className="calc-custom-btn" onClick={() => navigate('/contact')}>
                                Let's Discuss Your Idea <FaArrowRight />
                            </button>
                        </motion.div>
                    ) : (
                        <>
                            {/* Step 2: Features */}
                            <div className="calc-step">
                                <div className="step-label">
                                    <span className="step-num">02</span> Select your features
                                    {selectedFeats.length > 0 && (
                                        <span className="feat-count">{selectedFeats.length} selected</span>
                                    )}
                                </div>
                                <AnimatePresence mode="wait">
                                    <motion.div
                                        key={projectType + platform}
                                        className="calc-features-grid"
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        transition={{ duration: 0.2 }}
                                    >
                                        {features.map(feat => (
                                            <button
                                                key={feat.id}
                                                className={`calc-feat-btn ${selectedFeats.includes(feat.id) ? 'active' : ''}`}
                                                onClick={() => toggleFeature(feat.id)}
                                            >
                                                <div className="feat-btn-top">
                                                    <span className="feat-btn-icon">{feat.icon}</span>
                                                    <span className="feat-btn-check">
                                                        {selectedFeats.includes(feat.id) ? <FaCheck /> : <span>+</span>}
                                                    </span>
                                                </div>
                                                <span className="feat-btn-label">{feat.label}</span>
                                                <span className="feat-btn-price">+₹{feat.price.toLocaleString('en-IN')}</span>
                                            </button>
                                        ))}
                                    </motion.div>
                                </AnimatePresence>
                            </div>

                            {/* Step 3: Timeline */}
                            <div className="calc-step">
                                <div className="step-label"><span className="step-num">03</span> How soon do you need it?</div>
                                <div className="calc-timeline-grid">
                                    {TIMELINES.map(tl => (
                                        <button
                                            key={tl.id}
                                            className={`calc-timeline-btn ${timeline === tl.id ? 'active' : ''}`}
                                            onClick={() => setTimeline(tl.id)}
                                            style={{ '--tl-color': tl.color }}
                                        >
                                            <span className="tl-icon" style={{ color: tl.color }}>{tl.icon}</span>
                                            <span className="tl-label">{tl.label}</span>
                                            <span className="tl-sub">{tl.sub}</span>
                                            {tl.multiplier > 1 && (
                                                <span className="tl-surcharge" style={{ color: tl.color }}>
                                                    +{Math.round((tl.multiplier - 1) * 100)}% surcharge
                                                </span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* ── RIGHT: Live Price Panel ── */}
                <div className="calc-right">
                    <div className="calc-summary">
                        <div className="calc-summary-header">
                            <span>Your Estimate</span>
                            <span className="calc-summary-type">
                                {CALC_TYPES.find(t => t.id === projectType)?.label}
                            </span>
                        </div>

                        {isCustom ? (
                            /* Custom project — no price, just CTA */
                            <div className="calc-custom-right">
                                <div className="calc-custom-right-icon"><FaMagic /></div>
                                <p className="calc-custom-right-title">Custom Pricing</p>
                                <p className="calc-custom-right-sub">
                                    Your project is unique — pricing is scoped after a free discovery call.
                                    No commitment required.
                                </p>
                                <button className="calc-cta-btn" onClick={() => navigate('/contact')}>
                                    Request a Custom Quote <FaArrowRight />
                                </button>
                                <p className="calc-disclaimer">
                                    * Free 30-min consultation · No commitment required
                                </p>
                            </div>
                        ) : (
                            <>
                                <div className="calc-price-display">
                                    <span className="calc-price-symbol">₹</span>
                                    <span className="calc-price-value">
                                        <AnimatedPrice value={total} />
                                    </span>
                                </div>
                                <p className="calc-price-note">Estimated starting price · excl. GST</p>

                                <div className="calc-breakdown">
                                    <div className="breakdown-row">
                                        <span>Base price</span>
                                        <span>₹{base.toLocaleString('en-IN')}</span>
                                    </div>

                                    {selectedFeaturesList.map(f => (
                                        <div key={f.id} className="breakdown-row feature-row">
                                            <span>{f.label}</span>
                                            <span>+₹{f.price.toLocaleString('en-IN')}</span>
                                        </div>
                                    ))}

                                    {multiplier > 1 && (
                                        <div className="breakdown-row surcharge-row">
                                            <span style={{ color: selectedTL?.color }}>
                                                {selectedTL?.icon} {selectedTL?.label} surcharge
                                            </span>
                                            <span style={{ color: selectedTL?.color }}>×{multiplier}</span>
                                        </div>
                                    )}

                                    <div className="breakdown-total">
                                        <span>Total Estimate</span>
                                        <span>₹<AnimatedPrice value={total} /></span>
                                    </div>
                                </div>

                                <button className="calc-cta-btn pay-now-btn" onClick={() => setShowPayForm(true)}>
                                    <FaRupeeSign /> Pay Now &amp; Book Project
                                </button>

                                <button className="calc-pdf-btn" onClick={generateQuotePDF}>
                                    Download Quote PDF <FaArrowRight />
                                </button>

                                <p className="calc-disclaimer">
                                    * This is an estimate only. Final price confirmed after a free 30-min consultation.
                                </p>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Pay Now Modal ── */}
            <PayNowModal
                isOpen={showPayForm}
                onClose={() => setShowPayForm(false)}
                quoteData={{
                    typeLabel:    CALC_TYPES.find(t => t.id === projectType)?.label || '',
                    platformLabel: needsPlatform
                        ? ` — ${PLATFORMS[projectType]?.find(p => p.id === platform)?.label || ''}`
                        : '',
                    tlLabel:      selectedTL?.label || 'Standard',
                    tlSub:        selectedTL?.sub   || '3 – 5 days',
                    features:     selectedFeaturesList.map(f => f.label).join(', ') || 'None',
                    featuresList: selectedFeaturesList,
                    total,
                }}
            />
        </section>
    );
};

/* ─── FAQ ITEM ─── */
const FAQItem = ({ faq, index }) => {
    const [open, setOpen] = useState(false);
    return (
        <motion.div
            className={`faq-item ${open ? 'open' : ''}`}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.07 }}
        >
            <button className="faq-question" onClick={() => setOpen(!open)}>
                <span>{faq.q}</span>
                <FaChevronDown className={`faq-arrow ${open ? 'rotated' : ''}`} />
            </button>
            <AnimatePresence initial={false}>
                {open && (
                    <motion.div
                        className="faq-answer"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.28, ease: 'easeInOut' }}
                    >
                        <p>{faq.a}</p>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

/* ─── MAIN COMPONENT ─── */
const Pricing = () => {
    useSEO({
        title:       "Pricing | Web & App Development Cost Estimator | Nimai Solutions",
        description: "Build your own custom quote — select your project type, features and timeline to get an instant estimate. Web from ₹6,999, Mobile from ₹59,999. No hidden charges.",
        canonical:   `${SITE_URL}/pricing`,
        keywords:    "web development price Vadodara, mobile app development cost India, UI UX design pricing Gujarat, custom project cost estimator",
        schema:      PRICING_SCHEMA,
    });

    return (
        <div className="pricing-container">
            <Navbar />

            {/* ── HERO ── */}
            <section className="pricing-hero">
                <div className="pricing-hero-grid" />
                <motion.div
                    className="pricing-hero-content"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                >
                    <span className="pricing-badge">Instant Estimate</span>
                    <h1>Know the Cost <span>Before You Ask.</span></h1>
                    <p>Pick your project type, tick the features you need, choose your timeline — get a live price estimate in seconds.</p>
                </motion.div>
            </section>

            {/* ── CALCULATOR ── */}
            <PricingCalculator />

            {/* ── FAQ ── */}
            <section className="pricing-faq-section">
                <motion.div
                    className="faq-header"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                >
                    <span className="section-badge">Got Questions?</span>
                    <h2>Frequently Asked <span>Questions</span></h2>
                </motion.div>

                <div className="faq-list">
                    {FAQS.map((faq, i) => (
                        <FAQItem key={i} faq={faq} index={i} />
                    ))}
                </div>
            </section>

            {/* ── CTA ── */}
            <section className="pricing-cta-section">
                <div className="pricing-cta-card">
                    <div className="cta-blob blob-1" />
                    <div className="cta-blob blob-2" />
                    <motion.div
                        className="pricing-cta-inner"
                        initial={{ opacity: 0, scale: 0.96 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.7 }}
                    >
                        <span className="cta-tag">Free Consultation</span>
                        <h2>Not sure which plan is <span>right for you?</span></h2>
                        <p>Tell us about your project and we'll recommend the best approach — no commitment required.</p>
                        <div className="cta-actions">
                            <button className="cta-primary" onClick={() => window.location.href = '/contact'}>
                                Book a Free Call
                            </button>
                            <button className="cta-secondary" onClick={() => window.location.href = '/portfolio'}>
                                View Our Work
                            </button>
                        </div>
                    </motion.div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default Pricing;
